/**
 * Launcher type constants
 */
export const LAUNCHER_TYPE = {
  DCC: 'dcc',
  UTILITY: 'utility',
  SCRIPT: 'script',
  WEB: 'web'
};

/**
 * LauncherData class for handling launcher objects
 */
export class LauncherData {
  static TYPES = LAUNCHER_TYPE;

  constructor(data) {
    this.launcher_id = data.launcher_id;
    this.name = data.name || '';
    this.display_name = data.display_name || '';
    this.category = data.category || 'DCC';
    this.launcher_type = data.launcher_type || LAUNCHER_TYPE.DCC;
    this.icon_url = data.icon_url || null;
    this.thumbnail_updated_at = data.thumbnail_updated_at || null;
    this.description = data.description || '';
    this.is_enabled = data.is_enabled ?? true;
    this.profiles = (data.profiles || []).map(p => new ProfileData(p));
    this.space_ids = data.space_ids || [];
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
  }

  /**
   * Check if launcher has any profiles
   */
  get hasProfiles() {
    return this.profiles.length > 0;
  }

  /**
   * Get default profile if one exists
   */
  get defaultProfile() {
    return this.profiles.find(p => p.is_default) || this.profiles[0] || null;
  }

  /**
   * Get profiles available on a specific machine
   */
  getAvailableProfiles(machineCapabilities) {
    const installedProfileIds = new Set(
      (machineCapabilities || []).map(c => c.profile_id)
    );
    return this.profiles.filter(p => installedProfileIds.has(p.profile_id));
  }

  /**
   * Check if launcher is available on a machine
   */
  isAvailableOn(machineCapabilities) {
    return this.getAvailableProfiles(machineCapabilities).length > 0;
  }

  /**
   * Convert to plain object for state management
   */
  toPlainObject() {
    return {
      launcher_id: this.launcher_id,
      name: this.name,
      display_name: this.display_name,
      category: this.category,
      launcher_type: this.launcher_type,
      icon_url: this.icon_url,
      thumbnail_updated_at: this.thumbnail_updated_at,
      description: this.description,
      is_enabled: this.is_enabled,
      profiles: this.profiles.map(p => 
        typeof p.toPlainObject === 'function' ? p.toPlainObject() : p
      ),
      space_ids: this.space_ids,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  static fromApiResponse(data) {
    return new LauncherData(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(LauncherData.fromApiResponse);
  }
}

/**
 * ProfileData class for launch profiles
 */
export class ProfileData {
  constructor(data) {
    this.profile_id = data.profile_id;
    this.launcher_id = data.launcher_id;
    this.name = data.name || '';
    this.version = data.version || null;
    
    // Launch commands (new schema - Phase 7.1)
    this.windows_command = data.windows_command || null;
    this.linux_command = data.linux_command || null;
    this.macos_command = data.macos_command || null;
    
    // Validation paths (new schema - Phase 7.1)
    this.windows_paths = data.windows_paths || [];
    this.linux_paths = data.linux_paths || [];
    this.macos_paths = data.macos_paths || [];
    
    // Launch configuration
    this.launch_args = data.launch_args || [];
    this.environment_vars = data.environment_vars || {};
    
    // State
    this.is_default = data.is_default ?? false;
    this.is_enabled = data.is_enabled ?? true;
    
    // Timestamps
    this.created_at = data.created_at || null;
    this.updated_at = data.updated_at || null;
  }

  /**
   * Convert to plain object for API calls
   */
  toPlainObject() {
    return {
      profile_id: this.profile_id,
      launcher_id: this.launcher_id,
      name: this.name,
      version: this.version,
      windows_command: this.windows_command,
      linux_command: this.linux_command,
      macos_command: this.macos_command,
      windows_paths: this.windows_paths,
      linux_paths: this.linux_paths,
      macos_paths: this.macos_paths,
      launch_args: this.launch_args,
      environment_vars: this.environment_vars,
      is_default: this.is_default,
      is_enabled: this.is_enabled
    };
  }

  static fromApiResponse(data) {
    return new ProfileData(data);
  }
}






